// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DataTableBody, DataTableHead } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TableCounter from "@steiner/App/Components/Common/TableCounter";
import CustomerPartNumberItem from "@steiner/App/Components/CustomerPartNumbers/CustomerPartNumberItem";
import CustomerPartNumberItemWithoutProduct from "@steiner/App/Components/CustomerPartNumbers/CustomerPartNumberItemWithoutProduct";
import Notify from "@steiner/App/Components/Notify";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";
import DataTable, { DataTableProps } from "@steiner/MobiusOverrides/DataTable";
import DataTableHeader from "@steiner/MobiusOverrides/DataTable/DataTableHeader";
import qs from "qs";
import React, { useEffect } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const labels = {
    productDescription: translate("Product Description"),
    yourPartNumber: translate("Your Part #"),
    edit: translate("Edit"),
    delete: translate("Delete"),
    addToCart: translate("Add to Cart"),
    addToList: translate("Add to List"),
};
enum fields {
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
}
export interface OwnProps extends WidgetProps {
    fields: {
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
    };
}

export interface CustomerPartNumbersStyles {
    centeringWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    downloadLinksWrapper?: InjectableCss;
    csvFileLink?: LinkPresentationProps;
    textFileLink?: LinkPresentationProps;
    notFoundMessage?: TypographyProps;
    notFoundContainer?: InjectableCss;
}

export const tableStyles: CustomerPartNumbersStyles = {};

const styles = tableStyles;

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    return {
        customerProducts: state.xCustomerPartNumbers.customerProducts,
        isLoading: state.xCustomerPartNumbers.isLoading,
        paginationDataView: state.xCustomerPartNumbers.pagination,
        customerNumber: getBillToState(state, getSession(state).billToId).value?.customerNumber,
        editCustomerPartNumberPageUrl: getPageLinkByPageType(state, "CustomerPartNumberPage")?.url,
        products: getProductsForProductInfoList(state, ownProps.id),
        exportCustomerPartNumbersUrl: getSettingsCollection(state).customSharedSettings.exportCustomerPartNumbers,
        customerPartNumber: state.xCustomerPartNumbers.customerPartNumber,
    };
};

const mapDispatchToProps = {
    loadProductInfoList,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export const CustomerPartNumbers: FC<Props> = ({
    customerProducts,
    isLoading,
    paginationDataView,
    customerNumber,
    id: widgetId,
    products,
    fields,
    loadProductInfoList,
    customerPartNumber,
    exportCustomerPartNumbersUrl,
}) => {
    useEffect(() => {
        if (!customerProducts || customerProducts.length === 0) {
            return;
        }

        const productErps = customerProducts
            .filter(product => !!product.productErpNumber)
            .map(product => product.productErpNumber!);

        loadProductInfoList({
            id: widgetId,
            forceLoad: true,
            getProductCollectionParameter: {
                productNumbers: productErps,
            },
        });
    }, [customerProducts]);

    const getFileDownloadLink = (type: string) => {
        const queryString = qs.stringify({ fileType: type, customerNumber, customerPartNumber });
        return `${exportCustomerPartNumbersUrl}${queryString !== "" ? `?${queryString}` : ""}`;
    };

    if ((!customerProducts || customerProducts.length === 0) && !isLoading) {
        return (
            <StyledWrapper {...styles.notFoundContainer}>
                <Notify {...styles.notFoundMessage}>{translate("No customer products found.")}</Notify>
            </StyledWrapper>
        );
    }

    const pagination = paginationDataView || undefined;
    return (
        <TableCounter pagination={pagination} accordionOnSmallScreen={false}>
            <StyledWrapper {...styles.downloadLinksWrapper}>
                <Link {...styles.csvFileLink} href={getFileDownloadLink("csv")} target="_blank">
                    {translate("CSV")}
                </Link>
                <Link {...styles.textFileLink} href={getFileDownloadLink("txt")} target="_blank">
                    {translate("Text")}
                </Link>
            </StyledWrapper>

            {isLoading ? (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner size={24} />
                </StyledWrapper>
            ) : (
                <DataTable {...styles.dataTable}>
                    <DataTableHead>
                        <DataTableHeader>{labels.productDescription}</DataTableHeader>
                        <DataTableHeader>{labels.yourPartNumber}</DataTableHeader>
                        <DataTableHeader>{labels.edit}</DataTableHeader>
                        {fields.showAddToCart && <DataTableHeader>{labels.addToCart}</DataTableHeader>}
                        {fields.showAddToList && <DataTableHeader>{labels.addToList}</DataTableHeader>}
                    </DataTableHead>
                    <DataTableBody>
                        {customerProducts?.map(customerProduct => {
                            const product = products?.find(
                                product => product.productNumber === customerProduct.productErpNumber,
                            );
                            if (product) {
                                return (
                                    <CustomerPartNumberItem
                                        key={customerProduct.productErpNumber}
                                        customerProduct={customerProduct}
                                        product={product}
                                        labels={labels}
                                        showAddToCart={fields.showAddToCart}
                                        showAddToList={fields.showAddToList}
                                        widgetId={widgetId}
                                    />
                                );
                            }
                            return (
                                <CustomerPartNumberItemWithoutProduct
                                    key={customerProduct.productErpNumber}
                                    customerProduct={customerProduct}
                                    labels={labels}
                                    showAddToCart={fields.showAddToCart}
                                    showAddToList={fields.showAddToList}
                                />
                            );
                        })}
                    </DataTableBody>
                </DataTable>
            )}
        </TableCounter>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(CustomerPartNumbers)),
    definition: {
        group: "Customer Part Number" as any,
        allowedContexts: ["CustomerPartNumberPage"],
        fieldDefinitions: [
            {
                name: fields.showAddToCart,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showAddToList,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
